import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { GuiaDto, OcrResult, SaveGuiaRequest } from '@/types';
import { useObras } from '@/features/obras/hooks';
import { findObraMatch } from '@/features/obras/match';
import { useSaveGuia } from '@/features/guias/hooks';
import { checkGuiaNumero } from '@/features/guias/api';
import { ApiError } from '@/lib/apiClient';
import { parseDecimal, ptDateToIso, todayIso, formatEur } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Field, inputClass } from '@/components/ui/Field';
import { ErrorBanner } from '@/components/ui/ErrorBanner';

const OTHER = '__outra__';

interface FormArtigo {
  descricao: string;
  quantidade: string;
  valor: string;
}

interface FormState {
  data: string;
  numero: string;
  obra: string;
  obraFree: boolean;
  centroCusto: string;
  ccFree: boolean;
  artigos: FormArtigo[];
}

function fromGuia(guia: GuiaDto): FormState {
  return {
    data: guia.data,
    numero: guia.numero,
    obra: guia.obra,
    obraFree: false,
    centroCusto: guia.centroCusto ?? '',
    ccFree: false,
    artigos: guia.artigos.map((a) => ({
      descricao: a.descricao,
      quantidade: a.quantidade ? String(a.quantidade) : '',
      valor: a.valor ? String(a.valor) : '',
    })),
  };
}

function fromOcr(ocr: OcrResult): FormState {
  return {
    data: ptDateToIso(ocr.data),
    numero: ocr.numero,
    obra: ocr.obra,
    obraFree: false,
    centroCusto: ocr.centroCusto,
    ccFree: false,
    artigos:
      ocr.artigos.length > 0
        ? ocr.artigos.map((a) => ({
            descricao: a.descricao,
            quantidade: a.quantidade ? String(a.quantidade) : '',
            valor: a.valor ? String(a.valor) : '',
          }))
        : [{ descricao: '', quantidade: '', valor: '' }],
  };
}

const emptyForm = (): FormState => ({
  data: todayIso(),
  numero: '',
  obra: '',
  obraFree: false,
  centroCusto: '',
  ccFree: false,
  artigos: [{ descricao: '', quantidade: '', valor: '' }],
});

export function GuiaFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const state = location.state as { guia?: GuiaDto; ocr?: OcrResult } | null;

  const obrasQuery = useObras();
  const saveGuia = useSaveGuia();

  const editing = Boolean(id);
  const [form, setForm] = useState<FormState>(() => {
    if (state?.guia) return fromGuia(state.guia);
    if (state?.ocr) return fromOcr(state.ocr);
    return emptyForm();
  });
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<GuiaDto | null>(null);

  const obras = obrasQuery.data?.map((o) => o.nome) ?? [];

  // Warn if a guia with the same número already exists (across all months).
  useEffect(() => {
    const numero = form.numero.trim();
    if (!numero) {
      setDuplicate(null);
      return;
    }
    const handle = setTimeout(() => {
      checkGuiaNumero(numero, id)
        .then((res) => setDuplicate(res.exists ? res.existing : null))
        .catch(() => setDuplicate(null));
    }, 400);
    return () => clearTimeout(handle);
  }, [form.numero, id]);

  // Once the catalogue loads, if the read/entered obra is a variant of an existing
  // one, snap it to that existing obra (selected in the list) instead of creating
  // a new free-text variant. Only affects non-exact, non-free values.
  useEffect(() => {
    const names = obrasQuery.data?.map((o) => o.nome) ?? [];
    if (names.length === 0) return;
    setForm((f) => {
      if (!f.obra.trim() || f.obraFree || names.includes(f.obra)) return f;
      const match = findObraMatch(f.obra, names);
      return match && match !== f.obra ? { ...f, obra: match, obraFree: false } : f;
    });
  }, [obrasQuery.data]);

  const total = useMemo(
    () => form.artigos.reduce((sum, a) => sum + parseDecimal(a.valor), 0),
    [form.artigos],
  );

  const patch = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));
  const setArtigo = (i: number, p: Partial<FormArtigo>) =>
    setForm((f) => ({ ...f, artigos: f.artigos.map((a, j) => (j === i ? { ...a, ...p } : a)) }));

  const obraSelectValue = form.obraFree
    ? OTHER
    : obras.includes(form.obra)
      ? form.obra
      : form.obra
        ? OTHER
        : '';
  const showObraFree = form.obraFree || (!!form.obra && !obras.includes(form.obra));

  async function handleSave() {
    setError(null);
    if (!form.obra.trim()) {
      setError(t('review.needObra'));
      return;
    }
    const artigos = form.artigos
      .filter((a) => a.descricao.trim() || parseDecimal(a.valor))
      .map((a) => ({
        descricao: a.descricao.trim(),
        quantidade: parseDecimal(a.quantidade),
        valor: parseDecimal(a.valor),
      }));
    if (artigos.length === 0) {
      setError(t('review.needArticle'));
      return;
    }
    if (duplicate && !window.confirm(t('review.duplicateConfirm', { numero: form.numero.trim() }))) {
      return;
    }

    const body: SaveGuiaRequest = {
      data: form.data,
      numero: form.numero.trim() || undefined,
      obra: form.obra.trim(),
      artigos,
    };

    try {
      const saved = await saveGuia.mutateAsync({ id, body });
      navigate(`/?mes=${saved.mes}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.network'));
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <Card className="flex flex-col gap-3 p-4">
        <h2 className="text-[17px] font-bold">
          {editing ? t('review.titleEdit') : t('review.titleNew')}
        </h2>

        <Field label={t('review.date')}>
          <input
            type="date"
            value={form.data}
            onChange={(e) => patch({ data: e.target.value })}
            className={inputClass}
          />
        </Field>

        <Field label={t('review.obra')}>
          <select
            value={obraSelectValue}
            onChange={(e) =>
              e.target.value === OTHER
                ? patch({ obraFree: true })
                : patch({ obra: e.target.value, obraFree: false })
            }
            className={inputClass}
          >
            <option value="">{t('review.chooseList')}</option>
            {obras.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
            <option value={OTHER}>✎ {t('review.other')}</option>
          </select>
          {showObraFree && (
            <input
              value={form.obra}
              onChange={(e) => patch({ obra: e.target.value })}
              placeholder={t('review.obraPlaceholder')}
              className={`${inputClass} mt-2`}
            />
          )}
        </Field>

        <Field label={t('review.guiaNo')}>
          <input
            value={form.numero}
            onChange={(e) => patch({ numero: e.target.value })}
            placeholder={t('review.guiaPlaceholder')}
            className={inputClass}
          />
        </Field>

        {duplicate && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
            {t('review.duplicateWarning', {
              numero: duplicate.numero,
              obra: duplicate.obra,
              data: duplicate.data.split('-').reverse().join('/'),
            })}
          </div>
        )}

      </Card>

      <Card className="flex flex-col gap-2.5 p-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-[17px] font-bold">{t('review.articles')}</h2>
          <span className="text-[15px] font-extrabold">{formatEur(total)}</span>
        </div>

        {form.artigos.map((a, i) => (
          <div key={i} className="grid grid-cols-[1fr_62px_84px_34px] items-center gap-1.5">
            <input
              value={a.descricao}
              onChange={(e) => setArtigo(i, { descricao: e.target.value })}
              placeholder={t('review.description')}
              className={`${inputClass} py-2.5 text-sm`}
            />
            <input
              value={a.quantidade}
              onChange={(e) => setArtigo(i, { quantidade: e.target.value })}
              placeholder={t('review.qty')}
              inputMode="decimal"
              className={`${inputClass} py-2.5 text-right text-sm`}
            />
            <input
              value={a.valor}
              onChange={(e) => setArtigo(i, { valor: e.target.value })}
              placeholder={t('review.total')}
              inputMode="decimal"
              className={`${inputClass} py-2.5 text-right text-sm`}
            />
            <button
              type="button"
              aria-label={t('common.remove')}
              onClick={() => patch({ artigos: form.artigos.filter((_, j) => j !== i) })}
              className="h-[38px] rounded-lg bg-danger-bg text-danger-text"
            >
              ✕
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => patch({ artigos: [...form.artigos, { descricao: '', quantidade: '', valor: '' }] })}
          className="rounded-lg bg-conduril-tint py-2.5 text-sm hover:brightness-95"
        >
          ＋ {t('review.addArticle')}
        </button>
      </Card>

      <ErrorBanner message={error} />

      <div className="flex gap-2.5">
        <Button variant="secondary" onClick={() => navigate(-1)} className="flex-1">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saveGuia.isPending} className="flex-[2]">
          {t('review.save')}
        </Button>
      </div>
    </div>
  );
}
